import { toArray } from '@/_helpers/to-array'
import type {
  TApplicationStatus,
  TExposedApplicationAggregateDTO,
  TListApplicationCardsInputDTO,
  TListApplicationCardsResponseBodyDTO,
} from '@doutorsim/shared/applications'
import type {
  TConfirmedDeclarationsDataDTO,
  TDataConfirmationResponseDTO,
  TExposedPersonDeclarationDTO,
} from '@doutorsim/shared/exposed/person-declarations'
import type { TExposedPersonDTO } from '@doutorsim/shared/exposed/persons'
import type { TPaginationMeta } from '@doutorsim/shared/pagination'
import { apiClient } from '../api'
import { type GetApplicationsParams, buildApplicationSearchParams } from './applications'

export type TStaffApplicationListItem = Pick<
  TExposedApplicationAggregateDTO,
  | 'id'
  | 'companyId'
  | 'facilityId'
  | 'createdAt'
  | 'updatedAt'
  | 'installmentCount'
  | 'proposal'
  | 'guarantors'
> & {
  person: Pick<TExposedPersonDTO, 'name' | 'cpf' | 'email' | 'phone'>
  status: TApplicationStatus
  requestedAmount: number
}

export type TStaffApplicationsPaginatedResponse = {
  data: TStaffApplicationListItem[]
  meta: TPaginationMeta
}

export type TStaffApplicationCardsResponse = Record<
  string,
  { data: TStaffApplicationListItem[]; meta: TPaginationMeta }
>

function mapApplicationToListItem(app: TExposedApplicationAggregateDTO): TStaffApplicationListItem {
  return {
    id: app.id,
    companyId: app.companyId,
    facilityId: app.facilityId,
    createdAt: app.createdAt,
    updatedAt: app.updatedAt,
    person: {
      name: app.person?.name ?? 'Unknown',
      cpf: app.person?.cpf ?? '',
      email: app.person?.email ?? '',
      phone: app.person?.phone ?? '',
    },
    guarantors: app.guarantors ?? [],
    status: app.status || 'pending-analysis',
    // Kept in cents (matches the API DTO and proposal amounts); formatted at the
    // display boundary with formatBRLFromCents.
    requestedAmount: app.requestedAmount ?? 0,
    installmentCount: app.installmentCount,
    proposal: app.proposal,
  }
}

class StaffApplicationsApi {
  private getBaseUrl() {
    return `/api/v1/staff/applications`
  }

  async getApplications(
    params?: GetApplicationsParams,
  ): Promise<TStaffApplicationsPaginatedResponse> {
    const baseUrl = this.getBaseUrl()
    const queryParams = buildApplicationSearchParams(params)
    const url = queryParams.toString() ? `${baseUrl}?${queryParams.toString()}` : baseUrl
    const { data: applications, meta } = await apiClient.get<TExposedApplicationAggregateDTO[]>(url)

    if (!applications) return { data: [], meta: { hasMore: false } }

    return {
      data: applications.map(mapApplicationToListItem),
      meta: meta ?? { hasMore: false },
    }
  }

  async getApplicationCards(
    params?: TListApplicationCardsInputDTO,
  ): Promise<TStaffApplicationCardsResponse> {
    const baseUrl = this.getBaseUrl()
    const queryParams = buildApplicationSearchParams({
      ...params,
      status: toArray(params?.status),
      facilityIds: toArray(params?.facilityIds),
    })
    if (params?.limitPerStatus) {
      queryParams.append('limitPerStatus', String(params.limitPerStatus))
    }
    const url = queryParams.toString()
      ? `${baseUrl}/cards?${queryParams.toString()}`
      : `${baseUrl}/cards`
    const { data: cardsRecord } = await apiClient.get<TListApplicationCardsResponseBodyDTO>(url)

    if (!cardsRecord) return {}

    const result: TStaffApplicationCardsResponse = {}
    for (const [status, bucket] of Object.entries(cardsRecord)) {
      result[status] = {
        data: bucket.data.map(mapApplicationToListItem),
        meta: bucket.meta,
      }
    }

    return result
  }

  async getApplicationCardsByStatus(
    status: TApplicationStatus,
    params?: GetApplicationsParams,
  ): Promise<TStaffApplicationsPaginatedResponse> {
    const baseUrl = this.getBaseUrl()
    const queryParams = buildApplicationSearchParams(params)
    const url = queryParams.toString()
      ? `${baseUrl}/cards/${status}?${queryParams.toString()}`
      : `${baseUrl}/cards/${status}`
    const { data: applications, meta } = await apiClient.get<TExposedApplicationAggregateDTO[]>(url)

    if (!applications) return { data: [], meta: { hasMore: false } }

    return {
      data: applications.map(mapApplicationToListItem),
      meta: meta ?? { hasMore: false },
    }
  }

  async getPersonDeclarations(applicationId: string): Promise<TExposedPersonDeclarationDTO[]> {
    const baseUrl = this.getBaseUrl()
    const { data } = await apiClient.get<TExposedPersonDeclarationDTO[]>(
      `${baseUrl}/${applicationId}/person-declarations`,
    )
    return data ?? []
  }

  async getDataConfirmation(applicationId: string): Promise<TDataConfirmationResponseDTO> {
    const baseUrl = this.getBaseUrl()
    const { data } = await apiClient.get<TDataConfirmationResponseDTO>(
      `${baseUrl}/${applicationId}/data-confirmation`,
    )
    return data ?? { declarations: [], officialData: null }
  }

  async updatePersonDeclarationsStatus(
    applicationId: string,
    body: {
      status: 'confirmed' | 'rejected'
      reason?: string
      confirmedData?: TConfirmedDeclarationsDataDTO
    },
  ): Promise<TExposedPersonDeclarationDTO[]> {
    const baseUrl = this.getBaseUrl()
    const { data } = await apiClient.patch<TExposedPersonDeclarationDTO[]>(
      `${baseUrl}/${applicationId}/person-declarations/status`,
      body,
    )
    return data ?? []
  }

  async updateApplicationStatus(
    applicationId: string,
    status: TApplicationStatus,
  ): Promise<Pick<TExposedApplicationAggregateDTO, 'status'>> {
    const baseUrl = this.getBaseUrl()
    const { data: app } = await apiClient.patch<TExposedApplicationAggregateDTO>(
      `${baseUrl}/${applicationId}/status`,
      { status },
    )

    return {
      status: app?.status || 'pending-analysis',
    }
  }
}

export const staffApplicationsApi = new StaffApplicationsApi()
